// Copyright (c) StrangeBee 2023

import { capitalizeFirstLetter, generateId } from '@Helpers/string';
import produce from 'immer';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { ReportDropableElement } from './ReportDropableElement';
import { ReportDropableItem } from './ReportDropableItem';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { classList } from '@Helpers/className';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { formMap } from './Forms';
import { ICONS } from '@Constants/iconsApp';
import { useLocalMessage } from '@Helpers/locale';
import { ReportTemplateWidget, ReportTemplateWidgetEnum } from 'thehive-sdk';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { widgetMap } from './Widgets';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { TextForm } from './Forms';
import { useDrawer } from '@Hooks/useDrawer';

import './ReportTemplateManage.scss';

type ElemType = { _id: string; type: ReportTemplateWidgetType; index?: number };

type DrawerDataType = {
    createTitle: string;
    editTitle: string;
    editMode?: boolean;
    icon?: React.ReactNode;
};

const availableWidgets = new Map<string, DrawerDataType>([
    [
        'text',
        {
            editTitle: 'report-template.widgets.form.textEdit',
            createTitle: 'report-template.widgets.form.text',
            icon: <ICONS.TextOutlined />,
        },
    ],
    [
        'image',
        {
            editTitle: 'report-template.widgets.form.imageEdit',
            createTitle: 'report-template.widgets.form.image',
            icon: <ICONS.FileImageOutlined />,
        },
    ],
    [
        'table',
        {
            editTitle: 'report-template.widgets.form.tableEdit',
            createTitle: 'report-template.widgets.form.table',
            icon: <ICONS.TableOutlined />,
        },
    ],
    [
        'list',
        {
            editTitle: 'report-template.widgets.form.listEdit',
            createTitle: 'report-template.widgets.form.list',
            icon: <ICONS.ListOutlined />,
        },
    ],
]);

const widgetTypes = Array.from(availableWidgets.keys());

interface ReportTemplateManageProps {
    reportTemplateId: string;
    header?: string;
    footer?: string;
    widgets: ReportTemplateWidget[];
    disabled?: boolean;
    previewMode: boolean;

    setWidgets: React.Dispatch<React.SetStateAction<ReportTemplateWidget[] | undefined>>;
    setHeader: React.Dispatch<React.SetStateAction<string>>;
    setFooter: React.Dispatch<React.SetStateAction<string>>;
    setPreviewMode: React.Dispatch<React.SetStateAction<boolean>>;
    setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
    onEdit: () => void;
    onCancel: () => void;
}

export type ReportTemplateWidgetType = (typeof widgetTypes)[number];

export const mapWidgetToType: Record<ReportTemplateWidgetEnum, ReportTemplateWidgetType> = {
    [ReportTemplateWidgetEnum.ObservableTable]: 'table',
    [ReportTemplateWidgetEnum.AlertTable]: 'table',
    [ReportTemplateWidgetEnum.TaskTable]: 'table',
    [ReportTemplateWidgetEnum.TTPTable]: 'table',
    [ReportTemplateWidgetEnum.CustomFields]: 'table',

    [ReportTemplateWidgetEnum.ObservableList]: 'list',
    [ReportTemplateWidgetEnum.AlertList]: 'list',
    [ReportTemplateWidgetEnum.TaskList]: 'list',
    [ReportTemplateWidgetEnum.TTPList]: 'list',

    [ReportTemplateWidgetEnum.Text]: 'text',
    [ReportTemplateWidgetEnum.Image]: 'image',
    [ReportTemplateWidgetEnum.Table]: 'table',
    [ReportTemplateWidgetEnum.List]: 'list',
};

export const ReportTemplateManage = ({
    reportTemplateId,
    header,
    footer,
    widgets,
    disabled = false,
    previewMode,
    setPreviewMode,
    setWidgets,
    setHeader,
    setFooter,
    setIsDirty,
    onEdit,
    onCancel,
}: ReportTemplateManageProps): ReactElement => {
    const i = useLocalMessage();
    const {
        visible: drawerVisible,
        setVisible: setDrawerVisible,
        setData: setWidgetDrawerData,
        data: drawerData,
    } = useDrawer<DrawerDataType>({
        initialData: {
            createTitle: 'report-template.widgets.new',
            editTitle: 'report-template.widgets.edit',
            icon: <ICONS.TextOutlined />,
            editMode: false,
        },
    });

    const [formElem, setFormElem] = useState<ReactElement>();

    const addNewBlock = useCallback(
        (itemId: ReportTemplateWidgetType, dragId: string): ElemType => ({
            _id: dragId,
            type: itemId,
        }),
        [],
    );

    const openForm = useCallback(
        (elem: ElemType, editMode = false) => {
            setFormElem(
                formMap[elem.type]?.({
                    defaultValues: elem,
                    onFinish: (values) => {
                        // Check block is in widget list
                        const foundWidgetIdx = widgets.findIndex((widget) => widget._id === elem._id);

                        values.title === '' && delete values.title; // back-end doesn't handle empty title
                        const newWidgetInstance =
                            foundWidgetIdx < 0
                                ? [...widgets, { ...values, _id: elem._id }]
                                : Object.values({ ...widgets, [foundWidgetIdx]: { ...values, _id: elem._id } });

                        setWidgets(newWidgetInstance);
                        setIsDirty(true);
                        setDrawerVisible(false);
                        setFormElem(undefined);
                    },
                    key: elem._id,
                    reportTemplateId,
                    onCancel: () => {
                        setDrawerVisible(false);
                        setFormElem(undefined);
                        widgets.map((item) => {
                            if (!item._kind) {
                                const newWidgetInstance = widgets.filter(
                                    (widget) => widget._id !== (item as ReportTemplateWidget)._id,
                                );
                                setWidgets(newWidgetInstance);
                            }
                        });
                    },
                }),
            );

            // Init title on edit mode
            const initialData = availableWidgets.get(elem.type);
            setWidgetDrawerData({ ...initialData, editMode } as DrawerDataType);

            setDrawerVisible(true);
        },
        [reportTemplateId, setDrawerVisible, setWidgetDrawerData, setWidgets, widgets, setIsDirty],
    );

    const onDuplicate = useCallback(
        (widget: ReportTemplateWidget) => {
            const elem = { ...widget, reportTemplateId, type: mapWidgetToType[widget._kind] };
            // Check block is in widget list and add one to put the duplicated one just below
            const foundWidgetIdx = widgets.findIndex((widget) => widget._id === elem._id) + 1;
            // give all properties for a widget
            const block = { ...elem, ...addNewBlock(elem.type, generateId()) };
            const newWidgetInstance = [...widgets];

            // for image widget it crashes, the widget gets a json format image and upload it but it's not a file format so it crashes
            // need to duplicate the widget without the form
            if (elem._kind === ReportTemplateWidgetEnum.Image) {
                newWidgetInstance.splice(foundWidgetIdx, 0, { ...block, _id: block._id });

                setWidgets(newWidgetInstance);
                setIsDirty(true);
            } else {
                setFormElem(
                    formMap[elem.type]?.({
                        defaultValues: elem,
                        onFinish: (values) => {
                            values.title === '' && delete values.title; // back-end doesn't handle empty title
                            newWidgetInstance.splice(foundWidgetIdx, 0, { ...values, _id: block._id });

                            setWidgets(newWidgetInstance);
                            setIsDirty(true);
                            setDrawerVisible(false);
                            setFormElem(undefined);
                        },
                        key: elem._id,
                        reportTemplateId,
                        onCancel: () => {
                            setDrawerVisible(false);
                            setFormElem(undefined);
                            widgets.map((item) => {
                                if (!item._kind) {
                                    const newWidgetInstance = widgets.filter(
                                        (widget) => widget._id !== (item as ReportTemplateWidget)._id,
                                    );
                                    setWidgets(newWidgetInstance);
                                }
                            });
                        },
                    }),
                );
                // Init title on edit mode
                const initialData = availableWidgets.get(elem.type);
                setWidgetDrawerData({ ...initialData, editMode: true } as DrawerDataType);

                setDrawerVisible(true);
            }
        },
        [reportTemplateId, setDrawerVisible, setWidgetDrawerData, setWidgets, widgets, setIsDirty, addNewBlock],
    );

    const handleAddWidgetButtonClick = (widgetType: ReportTemplateWidgetType) => () => {
        if (availableWidgets.has(widgetType)) {
            setWidgetDrawerData(availableWidgets.get(widgetType));
            const block = addNewBlock(widgetType, generateId());
            openForm(block);
        }
    };

    const handleEditWidgetButtonClick =
        ({ _id, _kind, ...data }: ReportTemplateWidget) =>
        () => {
            openForm(
                {
                    _id,
                    type: mapWidgetToType[_kind],
                    _kind,
                    ...(data as any),
                    reportTemplateId,
                },
                true,
            );
        };

    const handleEditStaticWidget = function (onFinish: (val: string) => void, template?: string, title?: string) {
        return () => {
            setFormElem(
                <TextForm
                    defaultValues={{
                        template: template || '',
                        _kind: ReportTemplateWidgetEnum.Text,
                    }}
                    onFinish={(values) => {
                        onFinish(values.template);
                        setIsDirty(true);
                        setDrawerVisible(false);
                        setFormElem(undefined);
                    }}
                    reportTemplateId={reportTemplateId}
                    onCancel={() => {
                        setDrawerVisible(false);
                        setFormElem(undefined);
                    }}
                    notRequired
                    noTitle
                />,
            );

            // Init title on edit mode
            const initialData = availableWidgets.get('text');
            setWidgetDrawerData({ ...initialData, editTitle: title, editMode: true } as DrawerDataType);

            setDrawerVisible(true);
        };
    };

    const mapKindtoTitle: Partial<Record<ReportTemplateWidgetEnum, ReactElement>> = useMemo(
        () => ({
            [ReportTemplateWidgetEnum.List]: (
                <>
                    <ICONS.ListOutlined />
                    {i('report-template.widgets.list')}
                </>
            ),
            [ReportTemplateWidgetEnum.Table]: (
                <>
                    <ICONS.TableOutlined />
                    {i('report-template.widgets.table')}
                </>
            ),
            [ReportTemplateWidgetEnum.ObservableTable]: (
                <>
                    <ICONS.ObservableIcon />
                    {i('report-template.widgets.observable-table')}
                </>
            ),
            [ReportTemplateWidgetEnum.AlertTable]: (
                <>
                    <ICONS.AlertIcon />
                    {i('report-template.widgets.alert-table')}
                </>
            ),
            [ReportTemplateWidgetEnum.TaskTable]: (
                <>
                    <ICONS.TaskIcon />
                    {i('report-template.widgets.task-table')}
                </>
            ),
            [ReportTemplateWidgetEnum.TTPTable]: (
                <>
                    <ICONS.TTPIcon />
                    {i('report-template.widgets.ttp-table')}
                </>
            ),
            [ReportTemplateWidgetEnum.CustomFields]: (
                <>
                    <ICONS.CustomFieldIcon />
                    {i('report-template.widgets.custom-fields')}
                </>
            ),
            [ReportTemplateWidgetEnum.ObservableList]: (
                <>
                    <ICONS.ObservableIcon />
                    {i('report-template.widgets.observable-list')}
                </>
            ),
            [ReportTemplateWidgetEnum.AlertList]: (
                <>
                    <ICONS.AlertIcon />
                    {i('report-template.widgets.alert-list')}
                </>
            ),
            [ReportTemplateWidgetEnum.TaskList]: (
                <>
                    <ICONS.TaskIcon />
                    {i('report-template.widgets.task-list')}
                </>
            ),
            [ReportTemplateWidgetEnum.TTPList]: (
                <>
                    <ICONS.TTPIcon />
                    {i('report-template.widgets.ttp-list')}
                </>
            ),
            [ReportTemplateWidgetEnum.Image]: (
                <>
                    <ICONS.FileImageOutlined />
                    {i('report-template.widgets.image')}
                </>
            ),
            [ReportTemplateWidgetEnum.Text]: (
                <>
                    <ICONS.TextOutlined />
                    {i('report-template.widgets.text')}
                </>
            ),
        }),
        [i],
    );

    return (
        <>
            <div className={classList('report-template-manage', { 'report-template-manage__edit': !disabled })}>
                {/* Add block menu */}
                {!disabled && (
                    <div className="report-template-manage__drop-list">
                        <div className="relative">
                            <div className="report-template-manage__drop-list__widgets">
                                {!previewMode &&
                                    Array.from(availableWidgets).map(([key, { icon }]) => (
                                        <ReportDropableItem
                                            disabled={disabled}
                                            id={key}
                                            rowIndex={-1}
                                            key={`${key}-dropable`}
                                            onDrop={(item) => {
                                                if (availableWidgets.has(item.id)) {
                                                    const widgetType = widgets[item.index === 9999 ? 0 : item.index];

                                                    if (widgetType) {
                                                        openForm(widgets[item.index === 9999 ? 0 : item.index] as any);
                                                    }
                                                }
                                            }}
                                        >
                                            <div
                                                className="report-template-manage__drop-list__item"
                                                onClick={handleAddWidgetButtonClick(key as ReportTemplateWidgetType)}
                                            >
                                                <THTooltip title={i('report-template.widgets.' + key)}>
                                                    <THButton shape="circle" icon={icon} />
                                                </THTooltip>
                                            </div>
                                        </ReportDropableItem>
                                    ))}
                            </div>

                            <div className="report-template-manage__drop-list__cta">
                                <THButton
                                    className="report-template-manage__drop-list__button"
                                    icon={previewMode ? <ICONS.EyeInvisibleIcon /> : <ICONS.EyeIcon />}
                                    shape="circle"
                                    onClick={() => setPreviewMode(!previewMode)}
                                />

                                <THButton
                                    className="report-template-manage__drop-list__button"
                                    icon={<ICONS.SaveOutlined />}
                                    shape="circle"
                                    onClick={onEdit}
                                />
                                <THButton
                                    className="report-template-manage__drop-list__button"
                                    icon={<ICONS.CloseIcon />}
                                    shape="circle"
                                    onClick={onCancel}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {/* content of report template */}
                {previewMode ? (
                    <div className="report-template-details__preview">
                        {header && <THMarkdown disabled borderless value={header} />}
                        {widgets.map(({ _id, _kind, title, ...data }, index) => {
                            return (
                                <div
                                    key={`report_template_widget_preview${_id || index}`}
                                    className="report-template-details__preview__item"
                                >
                                    {title && (
                                        <div className="report-template-manage__widget-list__item__content__title">
                                            <span>{title}</span>
                                        </div>
                                    )}
                                    {widgetMap[mapWidgetToType[_kind]]?.({ _kind, ...(data as any), reportTemplateId })}
                                </div>
                            );
                        })}
                        {footer && <THMarkdown disabled borderless value={footer} />}
                    </div>
                ) : (
                    <div className="report-template-manage__content">
                        {/* Header widget */}
                        <div
                            className={classList('report-template-manage__widget-list__item', 'undraggable', 'header', {
                                'report-template-manage__widget-list__item__edit': !disabled,
                            })}
                        >
                            {disabled && !header ? null : (
                                <div className="report-template-manage__widget-list__item__content">
                                    {!disabled && (
                                        <div className="report-template-manage__widget-list__item__content__entity">
                                            {i('report-template.widgets.header')}
                                        </div>
                                    )}

                                    <THMarkdown disabled borderless value={header} />
                                </div>
                            )}

                            {!disabled && (
                                <div className="report-template-manage__widget-list__item__actions">
                                    <THButton
                                        type="link"
                                        color="black"
                                        onClick={handleEditStaticWidget(
                                            setHeader,
                                            header,
                                            `report-template.widgets.${header?.length === 0 ? 'new' : 'edit'}-header`,
                                        )}
                                        size="small"
                                    >
                                        <ICONS.EditIcon />
                                    </THButton>
                                </div>
                            )}
                        </div>

                        {/* Widget list */}
                        {widgets.length ? (
                            widgets.map((widget: ReportTemplateWidget, index) => {
                                const { _id, _kind, title, ...data } = widget;

                                const children = (
                                    <div
                                        key={_id}
                                        className={classList('report-template-manage__widget-list__item', {
                                            'report-template-manage__widget-list__item__edit': !disabled,
                                        })}
                                    >
                                        <div
                                            className="report-template-manage__widget-list__item__content"
                                            draggable={!disabled}
                                        >
                                            {!disabled &&
                                                !!mapKindtoTitle[_kind ?? capitalizeFirstLetter(data.type)] && (
                                                    <div className="report-template-manage__widget-list__item__content__entity report-template-manage__widget-list__item__content__entity--tagged">
                                                        {mapKindtoTitle[_kind ?? capitalizeFirstLetter(data.type)]}
                                                    </div>
                                                )}
                                            {
                                                <div className="report-template-manage__widget-list__item__content__title">
                                                    {title ? (
                                                        <span>{title}</span>
                                                    ) : (
                                                        <span className="report-template-manage__widget-list__item__content__title--not-specified">
                                                            {i('report-template.widgets.title.not-specified')}
                                                        </span>
                                                    )}
                                                </div>
                                            }
                                            {widgetMap[mapWidgetToType[_kind]]?.({
                                                _kind,
                                                ...(data as any),
                                                reportTemplateId,
                                            })}
                                        </div>

                                        {!disabled && (
                                            <div className="report-template-manage__widget-list__item__actions">
                                                <THButton
                                                    type="link"
                                                    color="black"
                                                    onClick={handleEditWidgetButtonClick(widget)}
                                                    size="small"
                                                >
                                                    <ICONS.EditIcon />
                                                </THButton>

                                                <THButton
                                                    onClick={() => onDuplicate(widget)}
                                                    color="black"
                                                    type="link"
                                                    icon={<ICONS.CopyOutlined />}
                                                    size="small"
                                                />

                                                <THButton
                                                    onClick={() =>
                                                        setWidgets(
                                                            produce((draft) => {
                                                                draft?.splice(index, 1);
                                                                return draft;
                                                            }),
                                                        )
                                                    }
                                                    color="black"
                                                    type="link"
                                                    icon={<ICONS.DeleteIcon />}
                                                    size="small"
                                                />
                                            </div>
                                        )}
                                    </div>
                                );

                                return !disabled ? (
                                    <ReportDropableElement
                                        disabled={disabled}
                                        id={_id}
                                        rowIndex={index}
                                        key={`report_dropable_element_${_id || index}`}
                                        moveRow={(dragIndex, hoverIndex, item) => {
                                            if (dragIndex === 9999) {
                                                return;
                                            }

                                            setWidgets(
                                                produce((draft) => {
                                                    const elem = draft?.[dragIndex];

                                                    if (elem) {
                                                        draft?.splice(dragIndex, 1);
                                                        draft?.splice(hoverIndex, 0, elem);
                                                        return draft;
                                                    } else {
                                                        const elem = addNewBlock(
                                                            item.id as ReportTemplateWidgetType,
                                                            item.dragId,
                                                        );
                                                        draft?.splice(hoverIndex, 0, elem as any);
                                                    }
                                                }),
                                            );
                                            setIsDirty(true);
                                        }}
                                    >
                                        {children}
                                    </ReportDropableElement>
                                ) : (
                                    children
                                );
                            })
                        ) : !disabled ? (
                            <ReportDropableElement
                                disabled={disabled}
                                id={generateId()}
                                rowIndex={9999}
                                key="empty"
                                moveRow={(dragIndex, hoverIndex, item) => {
                                    if (dragIndex === 9999) {
                                        return;
                                    }

                                    setWidgets(
                                        produce((draft) => {
                                            const elem = draft?.[dragIndex];

                                            if (elem) {
                                                draft?.splice(dragIndex, 1);
                                                draft?.splice(hoverIndex, 0, elem);
                                                return draft;
                                            } else {
                                                const elem = addNewBlock(
                                                    item.id as ReportTemplateWidgetType,
                                                    item.dragId,
                                                );
                                                draft?.splice(hoverIndex, 0, elem as any);
                                            }
                                        }),
                                    );

                                    setIsDirty(true);
                                }}
                            >
                                <DisplayEmptyData label={i('report-template.drop.empty')} />
                            </ReportDropableElement>
                        ) : (
                            <DisplayEmptyData label={i('report-template.drop.empty')} />
                        )}

                        {/* Footer */}
                        <div
                            className={classList('report-template-manage__widget-list__item', 'undraggable', {
                                'report-template-manage__widget-list__item__edit': !disabled,
                            })}
                        >
                            {disabled && !footer ? null : (
                                <div className="report-template-manage__widget-list__item__content">
                                    {!disabled && (
                                        <div className="report-template-manage__widget-list__item__content__entity">
                                            {i('report-template.widgets.footer')}
                                        </div>
                                    )}

                                    <THMarkdown disabled borderless value={footer} />
                                </div>
                            )}

                            {!disabled && (
                                <div className="report-template-manage__widget-list__item__actions">
                                    <THButton
                                        type="link"
                                        color="black"
                                        onClick={handleEditStaticWidget(
                                            setFooter,
                                            footer,
                                            `report-template.widgets.${footer?.length === 0 ? 'new' : 'edit'}-footer`,
                                        )}
                                        size="small"
                                    >
                                        <ICONS.EditIcon />
                                    </THButton>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <THDrawer
                title={drawerData ? i(drawerData.editMode ? drawerData.editTitle : drawerData.createTitle) : undefined}
                visible={drawerVisible}
                onChange={setDrawerVisible}
                className="drawer-form"
                disableClose
            >
                <>{formElem}</>
            </THDrawer>
        </>
    );
};
